import { type ReactNode } from "react";
import { Ban, CheckCircle2, Clock, Gavel, Hourglass, ShieldAlert, Timer, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { serverNow } from "@/lib/server-time";

/**
 * Canonical auction lifecycle states used across cards, detail pages,
 * dashboards and admin tables. Derived from the DB `auction_status` enum
 * plus two time-derived presentation states (ending soon / ended).
 */
export type AuctionStatusKind =
  | "live"
  | "ending_soon"
  | "ended"
  | "sold"
  | "reserve_not_met"
  | "pending"
  | "rejected"
  | "cancelled"
  | "draft"
  | "reserved";

type Tone = {
  className: string;
  label: string;
  icon: typeof Gavel;
};

const TONES: Record<AuctionStatusKind, Tone> = {
  live: {
    label: "Live",
    icon: Gavel,
    className: "border-emerald-500/35 bg-emerald-500/10 text-emerald-300",
  },
  ending_soon: {
    label: "Ending soon",
    icon: Timer,
    className: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  },
  ended: {
    label: "Ended",
    icon: Clock,
    className: "border-border/60 bg-muted/40 text-muted-foreground",
  },
  sold: {
    label: "Sold",
    icon: CheckCircle2,
    className: "border-gold/40 bg-gold/10 text-gold",
  },
  reserve_not_met: {
    label: "Reserve not met",
    icon: ShieldAlert,
    className: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  },
  pending: {
    label: "Pending review",
    icon: Hourglass,
    className: "border-sky-500/35 bg-sky-500/10 text-sky-300",
  },
  rejected: {
    label: "Rejected",
    icon: Ban,
    className: "border-destructive/40 bg-destructive/10 text-destructive",
  },
  cancelled: {
    label: "Cancelled",
    icon: Ban,
    className: "border-border/60 bg-muted/40 text-muted-foreground",
  },
  draft: {
    label: "Draft",
    icon: Clock,
    className: "border-border/60 bg-muted/40 text-muted-foreground",
  },
  reserved: {
    label: "Reserved",
    icon: Zap,
    className: "border-primary/40 bg-primary/10 text-primary",
  },
};

export type AuctionStatusInput = {
  status?: string | null;
  end_time?: string | null;
  reservation_expires_at?: string | null;
};

/**
 * Single source of truth for how an auction row maps to a display status.
 * `endingSoonMs` defaults to one hour and matches the countdown's amber state.
 */
export function resolveAuctionStatus(
  auction: AuctionStatusInput,
  endingSoonMs = 60 * 60 * 1000,
): AuctionStatusKind {
  const raw = (auction.status ?? "").toLowerCase();
  if (raw === "sold") return "sold";
  if (raw === "reserve_not_met") return "reserve_not_met";
  if (raw === "pending") return "pending";
  if (raw === "rejected") return "rejected";
  if (raw === "cancelled") return "cancelled";
  if (raw === "draft") return "draft";

  const endsAt = auction.end_time ? new Date(auction.end_time).getTime() : null;
  const now = serverNow();
  if (raw === "ended" || (endsAt !== null && endsAt <= now)) return "ended";

  if (
    auction.reservation_expires_at &&
    new Date(auction.reservation_expires_at).getTime() > now
  ) {
    return "reserved";
  }
  if (endsAt !== null && endsAt - now <= endingSoonMs) return "ending_soon";
  return "live";
}

/**
 * Shared status pill. Pass either a raw auction row (`auction`) or an already
 * resolved `kind`. Keep every status surface on this component so wording and
 * colours never drift between pages.
 */
export function StatusBadge({
  auction,
  kind,
  size = "sm",
  showIcon = true,
  label,
  className,
}: {
  auction?: AuctionStatusInput;
  kind?: AuctionStatusKind;
  size?: "xs" | "sm" | "md";
  showIcon?: boolean;
  label?: ReactNode;
  className?: string;
}) {
  const resolved = kind ?? resolveAuctionStatus(auction ?? {});
  const tone = TONES[resolved];
  const Icon = tone.icon;
  const sizing =
    size === "xs"
      ? "px-1.5 py-0.5 text-[10px] gap-1"
      : size === "md"
        ? "px-3 py-1 text-xs gap-1.5"
        : "px-2 py-0.5 text-[11px] gap-1";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border font-medium uppercase tracking-widest whitespace-nowrap",
        sizing,
        tone.className,
        resolved === "live" && "shadow-[0_0_18px_-8px_rgba(16,185,129,0.8)]",
        className,
      )}
    >
      {showIcon && <Icon className={size === "md" ? "h-3.5 w-3.5" : "h-3 w-3"} aria-hidden />}
      {label ?? tone.label}
    </span>
  );
}

export function statusLabel(kind: AuctionStatusKind) {
  return TONES[kind].label;
}
